import { gql } from '@apollo/client'
import { RichText } from '@farol-ds/react'
import { layoutStyles } from '@jusbrasil-web/docview/core'
import {
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useYouTubePlayer } from '../../hooks/use-youtube-player'
import { parseVideos } from '../../utils'
import { BlogDocument_DocumentFragment } from './__generated__/blog-document.graphql'
import styles from './blog-document.module.scss'

export interface BlogDocumentProps {
  document: BlogDocument_DocumentFragment
  contentRef: RefObject<HTMLDivElement>
  onVideoClick: (src: string) => void
}

export function BlogDocument(props: BlogDocumentProps) {
  const { document, contentRef, onVideoClick } = props

  const { text } = document
  const [processedText, setProcessedText] = useState(text || '')
  const contentRefInternal = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!text) return
    setProcessedText(
      parseVideos(
        text,
        styles['embedded-video-wrapper'],
        styles['embedded-video'],
        styles['embedded-video-overlay']
      )
    )
  }, [text, onVideoClick])

  useYouTubePlayer({
    containerRef: contentRefInternal,
    onVideoPlay: onVideoClick,
    contentKey: processedText,
  })

  const handleClick = ({ target }: ReactMouseEvent<HTMLDivElement>) => {
    const videoWrapper = (target as HTMLElement).closest('[data-video-embed]')
    if (!videoWrapper) return

    if (videoWrapper.hasAttribute('data-youtube-player-id')) return

    const overlay = videoWrapper.querySelector<HTMLElement>('[data-video-overlay]')
    if (overlay?.getAttribute('data-disabled')) return

    const src = videoWrapper.getAttribute('data-video-src') ?? ''
    onVideoClick(src)
    overlay?.setAttribute('data-disabled', 'true')
  }

  return (
    <RichText ref={contentRef} className={layoutStyles.contentWrapper}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- event delegation for video overlays inside dangerouslySetInnerHTML */}
      <article
        ref={contentRefInternal}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: processedText }}
      />
    </RichText>
  )
}

BlogDocument.fragments = {
  document: gql`
    fragment BlogDocument_document on Document {
      text(highlight: true)
    }
  `,
}
